<template>
  <section class="hero">
    <div class="hero__stage" :style="tilt">
      <!-- layer 1 — deepest: the graph the whole method rests on -->
      <div ref="l1" class="lay lay--grid" aria-hidden="true">
        <svg viewBox="0 0 600 760" preserveAspectRatio="xMidYMid meet">
          <g stroke="rgba(86,225,240,0.16)" stroke-width="1" fill="none">
            <line v-for="i in 11" :key="'v' + i" :x1="60 * i - 60 + 20" y1="20" :x2="60 * i - 60 + 20" y2="740" />
            <line v-for="i in 13" :key="'h' + i" x1="20" :y1="60 * i - 60 + 20" x2="620" :y2="60 * i - 60 + 20" />
          </g>
        </svg>
      </div>

      <!-- layer 2 — the shaded head -->
      <div ref="l2" class="lay lay--head">
        <HeadForm :light-x="0.7" :light-y="0.24" />
      </div>

      <!-- layer 3 — closest: the annotations an instructor would scribble -->
      <div ref="l3" class="lay lay--notes" aria-hidden="true">
        <figure class="note note--a">
          <span class="note__dot"></span>
          <figcaption><b>#5 full light</b><br />paper, untouched</figcaption>
        </figure>
        <figure class="note note--b">
          <span class="note__dot"></span>
          <figcaption><b>#4 reflected light</b><br />the rim that separates<br />shadow from cast shadow</figcaption>
        </figure>
        <figure class="note note--c">
          <span class="note__dot"></span>
          <figcaption><b>#1 cast shadow</b><br />the darkest value on the page</figcaption>
        </figure>
        <figure class="note note--d">
          <span class="note__dot"></span>
          <figcaption><b>one catchlight</b><br />half in the pupil,<br />half in the iris</figcaption>
        </figure>
      </div>
    </div>

    <div class="hero__type">
      <p class="hero__kicker mono">A graphite portrait lab · 10 chapters · scroll to begin</p>
      <h1 class="hero__title">
        <span class="l1">Make it</span>
        <span class="l2">look <em>real</em></span>
      </h1>
      <p class="hero__lede">
        Beginners don't fail at portraits because their hands are clumsy. They fail
        because they draw the <b>idea</b> of an eye instead of the shapes and values
        actually in front of them. This is the fix — a repeatable procedure that
        replaces outline-and-hope with <span class="sig">shapes, values, blending, lifting</span>.
      </p>
      <div class="hero__scale" aria-label="the five-value scale used throughout">
        <span v-for="(v, i) in scale" :key="i" class="chip" :style="{ background: v.c, color: i > 2 ? '#05070a' : '#e8ecf1' }">
          {{ v.n }}
        </span>
        <span class="hero__scale-lbl mono">the whole course lives on five values</span>
      </div>
      <a class="hero__cta" href="#switch">
        <span>Start with the switch</span>
        <svg width="26" height="10" viewBox="0 0 26 10" fill="none" aria-hidden="true">
          <path d="M0 5h24M20 1l4 4-4 4" stroke="currentColor" stroke-width="1.2" />
        </svg>
      </a>
    </div>

    <div class="hero__scrollhint mono" aria-hidden="true">
      <span>scroll</span>
      <i></i>
    </div>
  </section>
</template>

<script setup>
import HeadForm from '../svg/HeadForm.vue'
import { useParallax } from '../../composables/useParallax'
import { useViewport } from '../../composables/useViewport'
import { computed } from 'vue'

const vp = useViewport()
const l1 = useParallax(-0.55)
const l2 = useParallax(0.18)
const l3 = useParallax(0.85)

const scale = [
  { n: '#1', c: '#0a0c10' },
  { n: '#2', c: '#3a4048' },
  { n: '#3', c: '#767d87' },
  { n: '#4', c: '#b6bcc4' },
  { n: '#5', c: '#f4f6f8' }
]

/* mouse tilt on the stage — depth without motion sickness */
const tilt = computed(() => ({
  '--tx': (vp.pointerX * 12).toFixed(2) + 'px',
  '--ty': (vp.pointerY * 8).toFixed(2) + 'px'
}))
</script>

<style scoped>
.hero {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: clamp(24px, 4vw, 70px);
  padding: 120px var(--gutter) 90px;
  max-width: 1400px;
  margin: 0 auto;
}

/* ---------- stage ---------- */
.hero__stage {
  position: relative;
  order: 2;
  aspect-ratio: 3 / 4;
  max-height: 78vh;
  justify-self: center;
  width: 100%;
}
.lay {
  position: absolute;
  inset: 0;
  transform: translate3d(var(--tx, 0px), calc(var(--shift, 0px) + var(--ty, 0px)), 0);
  will-change: transform;
}
.lay--grid { --depth: -0.7; }
.lay--head { --depth: 0.35; }
.lay--notes { --depth: 1.1; }
.lay {
  transform: translate3d(
    calc(var(--tx, 0px) * var(--depth, 1)),
    calc(var(--shift, 0px) + var(--ty, 0px) * var(--depth, 1)),
    0
  );
}
.lay--grid {
  inset: -6% -10% -6% -10%;
  opacity: 0.5;
  mask-image: radial-gradient(60% 55% at 50% 42%, #000 20%, transparent 80%);
  -webkit-mask-image: radial-gradient(60% 55% at 50% 42%, #000 20%, transparent 80%);
}
.lay--grid svg { width: 100%; height: 100%; }
.lay--head {
  display: grid;
  place-items: center;
  filter: drop-shadow(0 40px 80px rgba(0, 0, 0, 0.65));
}
.lay--head :deep(.headform) { max-height: 100%; width: auto; max-width: 100%; }

/* ---------- annotations ---------- */
.note {
  position: absolute;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  line-height: 1.7;
  letter-spacing: 0.06em;
  color: var(--text-dim);
  text-transform: uppercase;
  white-space: nowrap;
}
.note b { color: var(--light); font-weight: 400; }
.note__dot {
  width: 6px;
  height: 6px;
  margin-top: 5px;
  border-radius: 50%;
  border: 1px solid var(--light);
  flex: none;
  position: relative;
}
.note__dot::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 34px;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 180, 92, 0.6), transparent);
}
.note--a { top: 14%; right: -6%; }
.note--a .note__dot::after { right: 100%; background: linear-gradient(270deg, rgba(255, 180, 92, 0.6), transparent); }
.note--b { top: 46%; left: -10%; text-align: right; flex-direction: row-reverse; }
.note--c { bottom: 15%; right: -4%; }
.note--c .note__dot::after { right: 100%; background: linear-gradient(270deg, rgba(255, 180, 92, 0.6), transparent); }
.note--d { top: 33%; left: -8%; text-align: right; flex-direction: row-reverse; }

/* ---------- type ---------- */
.hero__type { order: 1; max-width: 620px; }
.hero__kicker { color: var(--text-faint); margin-bottom: 26px; }

.hero__title {
  font-size: clamp(52px, 9vw, 128px);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 0.86;
  margin-bottom: 30px;
}
.hero__title span { display: block; }
.hero__title .l1 { color: var(--v3); }
.hero__title .l2 { color: var(--v5); }
.hero__title em {
  font-style: normal;
  background: linear-gradient(100deg, var(--light), #fff3e0 55%, var(--signal));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero__lede {
  font-size: clamp(15px, 0.7vw + 13px, 19px);
  color: var(--text-dim);
  max-width: 54ch;
  margin-bottom: 34px;
}
.hero__lede b { color: var(--v4); font-weight: 400; }
.hero__lede .sig { color: var(--signal); }

.hero__scale { display: flex; align-items: center; gap: 4px; margin-bottom: 38px; flex-wrap: wrap; }
.chip {
  width: 46px;
  height: 30px;
  display: grid;
  place-items: center;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  border: 1px solid rgba(255, 255, 255, 0.07);
}
.hero__scale-lbl { margin-left: 14px; color: var(--text-faint); font-size: 9.5px; }

.hero__cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--v5);
  border: 1px solid var(--edge);
  padding: 14px 22px;
  border-radius: 2px;
  transition: border-color 0.3s, background 0.3s, gap 0.3s;
}
.hero__cta:hover { border-color: var(--signal); background: var(--signal-dim); gap: 18px; }

.hero__scrollhint {
  position: absolute;
  left: var(--gutter);
  bottom: 26px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-faint);
  font-size: 9px;
}
.hero__scrollhint i {
  display: block;
  width: 44px;
  height: 1px;
  background: linear-gradient(90deg, var(--text-faint), transparent);
  position: relative;
  overflow: hidden;
}
.hero__scrollhint i::after {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 14px;
  background: var(--signal);
  animation: sweep 2.4s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}
@keyframes sweep {
  0% { transform: translateX(-16px); }
  100% { transform: translateX(46px); }
}

@media (max-width: 980px) {
  .hero {
    grid-template-columns: 1fr;
    padding-top: 96px;
    min-height: auto;
  }
  .hero__stage { order: 2; max-height: 60vh; margin-top: 30px; }
  .hero__type { order: 1; }
  .note { display: none; }
  .hero__scrollhint { display: none; }
}
</style>
